import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import type { InteractiveBadge, Prompt } from "@1js/pl-card-components";
import {
  BookmarkButtonTooltip,
  InteractivePromptBadges,
  PromptRemove,
  PromptShare,
  UnBookmarkButtonTooltip,
} from "@1js/pl-card-components";
import { isExtensionPrompt, type Group } from "@1js/pl-types";
import type { ButtonProps } from "@fluentui/react-components";
import {
  Button,
  Toaster,
  ToggleButton,
  Tooltip,
  makeStyles,
  shorthands,
  useId,
} from "@fluentui/react-components";
import type { FluentIconsProps } from "@fluentui/react-icons";
import {
  BookmarkFilled,
  BookmarkRegular,
  Copy20Regular,
  Share20Regular,
  bundleIcon,
} from "@fluentui/react-icons";
import parse from "html-react-parser";
import * as React from "react";
import { useShareToast } from "../../../hooks/toast/useShareToast";
import { useToast } from "../../../hooks/toast/useToast";
import { useFeatureEnablement } from "../../../hooks/useFeatureEnablement";
import { useFlightContext } from "../../../hooks/useFlightContext";
import { useHandoffService } from "../../../hooks/useHandoffService";
import { usePromptActions } from "../../../hooks/usePromptActions";
import { useSharePrompt } from "../../../hooks/useSharePrompt";
import { PropertyContext } from "../../../pages/PropertyContext.web";
import type { WebPrompt } from "../../data";
import { SignInToBookmark } from "../../SignInHeader/SignInHeader.web.string";
import {
  ActionsCopyPromptClipboard,
  ActionsCopyPromptTooltip,
  ActionsPromptBookmarkSaved,
  ActionsPromptBookmarkSavedError,
  ActionsPromptBookmarkTooltip,
  ActionsPromptBookmarkUnSaved,
  ActionsPromptBookmarkUnSavedError,
  ActionsPromptDeleteTooltip,
  ActionsPromptLinkClipboard,
  ActionsPromptLinkClipboardBody,
  ActionsPromptLinkTooltip,
  ActionsTryInLabel,
} from "./Actions.web.string";

type Props = {
  prompt: WebPrompt | Prompt;
  isBookmarkEnabled?: boolean;
  isBookmarked?: boolean;
  isPromptSavingEnabled?: boolean;
  isPromptSharingEnabled?: boolean;
  isPromptSharingViaEmailEnabled?: boolean;
  isPromptCopyLinkEnabled?: boolean;
  onBookmarkToggled?: (prompt: Prompt, value: boolean) => Promise<void>;
  onShareClicked?: (prompt: Prompt) => void;
  onCopyClicked?: (prompt: Prompt) => void;
  onDelete?: (prompt: Prompt) => void;
  onTryInAppBadgeAction?: (
    prompt: Prompt,
    product: string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  isPromptSharingToWorkgroupEnabled?: boolean;
  onShareToWorkgroupClicked?: (prompts: Prompt[]) => void | Promise<void>;
  groups?: Group[];
  selectedGroupId?: string;
  onRemoveFromWorkgroupClicked?: (
    prompts: Prompt[],
    groupIds: string[]
  ) => void;
  isPromptRemovingFromWorkgroupEnabled?: boolean;
};

const useStyles = makeStyles({
  mainContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  actionsIconContainer: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    ...shorthands.gap("8px"),
  },
  shareButton: {
    ...shorthands.padding("5px"),
  },
  removeButton: {
    ...shorthands.padding("5px"),
    boxSizing: "content-box",
  },
  actionsButtonContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "stretch",
    ...shorthands.gap("8px"),
  },
});

const BookmarkIcon = bundleIcon(BookmarkFilled, BookmarkRegular);

export const Actions: React.FC<Props> = ({
  prompt,
  isBookmarkEnabled,
  isBookmarked,
  isPromptSavingEnabled,
  isPromptSharingEnabled,
  isPromptSharingViaEmailEnabled,
  isPromptCopyLinkEnabled = true,
  onBookmarkToggled,
  onShareClicked,
  onCopyClicked,
  onDelete,
  onTryInAppBadgeAction,
  isPromptSharingToWorkgroupEnabled,
  onShareToWorkgroupClicked,
  groups,
  onRemoveFromWorkgroupClicked,
  selectedGroupId,
  isPromptRemovingFromWorkgroupEnabled,
}) => {
  const strings = useStrings({
    ActionsCopyPromptClipboard,
    ActionsCopyPromptTooltip,
    ActionsPromptLinkClipboard,
    ActionsPromptLinkClipboardBody,
    ActionsPromptLinkTooltip,
    ActionsPromptBookmarkTooltip,
    UnBookmarkButtonTooltip,
    BookmarkButtonTooltip,
    ActionsPromptBookmarkSaved,
    ActionsPromptBookmarkUnSaved,
    ActionsPromptBookmarkSavedError,
    ActionsPromptBookmarkUnSavedError,
    ActionsPromptDeleteTooltip,
  });

  const stringsWithPlaceholders = useStringsWithPlaceholders({
    ActionsTryInLabel,
  });

  const { isTryInOnAuthed } = useFeatureEnablement();

  const { isHandoffSupported, isProductNeedsFile } = useHandoffService();

  const badges: InteractiveBadge[] = prompt.Products.map((product) => {
    return {
      id: product,
      needsFile: isProductNeedsFile(product),
      onAction:
        typeof onTryInAppBadgeAction === "function" &&
        isHandoffSupported(product, prompt)
          ? (tagRef: React.MutableRefObject<null>) =>
              onTryInAppBadgeAction(prompt, product, tagRef)
          : undefined,
    };
  });

  const bookmarkTooltip = isBookmarked
    ? strings.UnBookmarkButtonTooltip()
    : strings.BookmarkButtonTooltip();

  const buttonStyleProps: ButtonProps = {
    appearance: "transparent",
  };

  const iconStyleProps: FluentIconsProps = {
    filled: isBookmarked,
  };

  const sharePrompt = useSharePrompt();

  const [vertical, setVertical] = React.useState(0);
  const toasterId = useId("toaster");
  const { notifyShareCopyLinkSuccess, notifyShareCopyLinkFail } =
    useShareToast(toasterId);
  const { notify: _notify } = useToast(toasterId, 5000, prompt.Title);
  const notify = (...props: Parameters<typeof _notify>) => {
    const dialog = document.getElementById("dialog-title-id");
    setVertical(dialog?.getBoundingClientRect().top || 108); // 108 is the default value for margin between the top of the page and the dialog.
    _notify(...props);
  };

  const styles = useStyles();

  const handleBookmarkClicked = () => {
    if (onBookmarkToggled) {
      onBookmarkToggled(prompt, !isBookmarked);
    }
  };
  const { handleCopyClicked: handleCopyClickedHooked } = usePromptActions();

  const handleCopyClicked = () => {
    handleCopyClickedHooked(prompt, (prompt: Prompt) => {
      notify("success", strings.ActionsCopyPromptClipboard());
      onCopyClicked?.(prompt);
    });
  };

  const handleShareViaCopyLinkClicked = async (prompt: Prompt) => {
    try {
      await sharePrompt.shareUrlViaCopyLink(prompt);
      notifyShareCopyLinkSuccess(prompt);
      if (onShareClicked) {
        onShareClicked(prompt);
      }
    } catch (_) {
      notifyShareCopyLinkFail();
    }
  };

  const handleShareViaEmailClicked = async (prompt: Prompt) => {
    await sharePrompt.shareUrlViaEmail(prompt);
    // TODO: we need a way to distinguish between the two types of sharing (copy link and email)
    if (onShareClicked) {
      onShareClicked(prompt);
    }
  };

  const isPromptRemovingFromWorkgroupEnabledInner =
    isPromptRemovingFromWorkgroupEnabled &&
    !!selectedGroupId &&
    prompt.Groups?.some((group) => group.Id === selectedGroupId);
  const isExtension = isExtensionPrompt(prompt);
  const [siteSetting] = React.useContext(PropertyContext);
  const { isFlightActive } = useFlightContext();

  const localizations = useStrings({
    SignInToBookmark,
  });

  return (
    <div className={styles.mainContainer}>
      <div className={styles.actionsButtonContainer}>
        {isTryInOnAuthed && (
          <InteractivePromptBadges
            badges={badges}
            maxNumber={20}
            displayMode="iconAndText"
            labelWrapper={stringsWithPlaceholders.ActionsTryInLabel}
            appearance="outline"
          />
        )}
      </div>
      <div className={styles.actionsIconContainer}>
        <Toaster
          toasterId={toasterId}
          offset={{ vertical: vertical }}
          pauseOnHover
          pauseOnWindowBlur
        />
        <Tooltip
          content={strings.ActionsCopyPromptTooltip()}
          relationship="label"
        >
          <Button
            data-testid="copy"
            icon={<Copy20Regular />}
            onClick={handleCopyClicked}
            {...buttonStyleProps}
          />
        </Tooltip>
        {!isExtension && isBookmarkEnabled && prompt.Origin !== "User" && (
          <Tooltip content={bookmarkTooltip} relationship="label">
            <ToggleButton
              data-testid="bookmark"
              checked={isBookmarked}
              icon={<BookmarkIcon {...iconStyleProps} />}
              onClick={handleBookmarkClicked}
              {...buttonStyleProps}
            />
          </Tooltip>
        )}
        {/* This is the code for putting in the fake book mark button that will encourage users to sign in. */}
        {isFlightActive("isSignInSuggestionEnabled") && !siteSetting.user && (
          <Tooltip
            content={parse(localizations.SignInToBookmark())}
            relationship="label"
          >
            <Button
              data-testid="unauthed-bookmark"
              icon={<BookmarkRegular {...iconStyleProps} />}
              {...buttonStyleProps}
              disabled={true}
            />
          </Tooltip>
        )}
        {!isExtension && isPromptSavingEnabled && prompt.Origin === "User" && (
          <Tooltip
            content={strings.ActionsPromptDeleteTooltip()}
            relationship="label"
          >
            <ToggleButton
              data-testid="delete"
              checked={true}
              icon={<BookmarkIcon {...iconStyleProps} />}
              onClick={() => onDelete && onDelete(prompt)}
              {...buttonStyleProps}
            />
          </Tooltip>
        )}
        {!isExtension && isPromptCopyLinkEnabled && isPromptSharingEnabled && (
          <PromptShare
            appearance="web"
            prompt={prompt}
            isPromptSharingViaEmailEnabled={isPromptSharingViaEmailEnabled}
            onShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
            onShareViaEmailClicked={handleShareViaEmailClicked}
            className={styles.shareButton}
            isPromptSharingToWorkgroupEnabled={
              isPromptSharingToWorkgroupEnabled
            }
            onShareToWorkgroupClicked={onShareToWorkgroupClicked}
            groups={groups}
          />
        )}
        {!isExtension &&
          isPromptCopyLinkEnabled &&
          !isPromptSharingEnabled &&
          prompt.Origin !== "User" && (
            <Tooltip
              content={strings.ActionsPromptLinkTooltip()}
              relationship="label"
            >
              <Button
                data-testid="share"
                icon={<Share20Regular />}
                onClick={() => {
                  handleShareViaCopyLinkClicked(prompt);
                }}
                {...buttonStyleProps}
              />
            </Tooltip>
          )}
        {!isExtension && isPromptRemovingFromWorkgroupEnabledInner && (
          <PromptRemove
            onRemove={onRemoveFromWorkgroupClicked}
            prompt={prompt}
            groupId={selectedGroupId}
            isRemoving={prompt.RemovingState === "long"}
            className={styles.removeButton}
          />
        )}
      </div>
    </div>
  );
};
